import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as DropdownIcon } from "../../asserts/logo/dropdown.svg";

const SEVERITIES = ["Critical", "High", "Medium", "Low"];
const LIKELIHOODS = ["High", "Medium", "Low"];
const OWNERS = [
  "Responsible AI Lead",
  "Risk Manager",
  "Security Governance Lead",
  "Privacy Officer",
  "QA Lead",
];

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    bgcolor: "#fff",
    fontSize: "16px",
    color: "#707070",
    "& fieldset": { borderColor: "#707070" },
    "&:hover fieldset": { borderColor: "#707070" },
    "&.Mui-focused fieldset": { borderColor: "#555555", borderWidth: 1 },
  },
};

const selectSx = {
  borderRadius: "4px",
  fontSize: "16px",
  color: "#707070",
  bgcolor: "#fff",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#707070" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#707070" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#555555",
    borderWidth: 1,
  },
};

function FieldLabel({ children }) {
  return (
    <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#707070", mb: 0.75 }}>
      {children}
    </Typography>
  );
}

function formFromRisk(risk) {
  return {
    description: risk.description || "",
    severity: risk.severity || "Medium",
    likelihood: risk.likelihood || "Medium",
    owner: risk.owner || "Responsible AI Lead",
  };
}

function formatLastEdited(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  } catch {
    return null;
  }
}

/**
 * Right-side drawer matching Figma edit-risk panel (Step 2 — Risk Register).
 */
export default function EditRiskDialog({ risk, onClose, onSave }) {
  const open = Boolean(risk);
  const [form, setForm] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (risk) setForm(formFromRisk(risk));
    else setForm(null);
  }, [risk]);

  const ownerOptions = useMemo(() => {
    if (!risk?.owner) return OWNERS;
    if (OWNERS.includes(risk.owner)) return OWNERS;
    return [risk.owner, ...OWNERS];
  }, [risk]);

  if (!open || !form) return null;

  const riskName = (risk.name || "").trim() || "Untitled risk";
  const lastEditedTime = formatLastEdited(risk.updatedAt);

  const handle = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = async () => {
    try {
      setSubmitting(true);
      await onSave({
        description: form.description.trim(),
        severity: form.severity,
        likelihood: form.likelihood,
        owner: form.owner,
        mitigation: form.mitigation,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480, md: 520 },
          maxWidth: "100vw",
          boxShadow: "-8px 0 32px rgba(16, 24, 40, 0.14)",
        },
      }}
      slotProps={{
        backdrop: {
          sx: { bgcolor: "rgba(52, 64, 84, 0.45)" },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#fff" }}>
        {/* Header */}
        <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: "1px solid #EAECF0" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
            <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#555555", lineHeight: 1.3, pr: 1 }}>
              {riskName}
            </Typography>
            <IconButton
              size="small"
              onClick={onClose}
              aria-label="Close"
              sx={{ color: "#667085", mt: -0.5, mr: -0.5, flexShrink: 0 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography sx={{ fontSize: "14px", color: "#555555", mt: 1, lineHeight: 1.5 }}>
            Changes are versioned. The previous values stay in the audit log.
          </Typography>
        </Box>

        {/* Form body */}
        <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 2.5 }}>
          <Stack spacing={2.5}>
            <Box>
              <FieldLabel>Description</FieldLabel>
              <TextField
                fullWidth
                multiline
                minRows={4}
                value={form.description}
                onChange={handle("description")}
                placeholder="Describe the failure mode, who is affected, and why it matters."
                sx={fieldSx}
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <Box>
                <FieldLabel>Severity</FieldLabel>
                <FormControl fullWidth size="small">
                  <Select IconComponent={DropdownIcon} value={form.severity} onChange={handle("severity")} sx={selectSx}>
                    {SEVERITIES.map((s) => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box>
                <FieldLabel>Likelihood</FieldLabel>
                <FormControl fullWidth size="small">
                  <Select IconComponent={DropdownIcon} value={form.likelihood} onChange={handle("likelihood")} sx={selectSx}>
                    {LIKELIHOODS.map((l) => (
                      <MenuItem key={l} value={l}>{l}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box>
              <FieldLabel>Owner</FieldLabel>
              <FormControl fullWidth size="small">
                <Select IconComponent={DropdownIcon} value={form.owner} onChange={handle("owner")} sx={selectSx}>
                  {ownerOptions.map((o) => (
                    <MenuItem key={o} value={o}>{o}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid #EAECF0",
            display: "flex",
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
            bgcolor: "#fff",
          }}
        >
          <Typography sx={{ fontSize: "13px", color: "#555555", lineHeight: "24px", flex: 1 }}>
            {lastEditedTime ? `Last edited ${lastEditedTime}` : "Not edited yet"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end", flexShrink: 0 }}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: "#1A6EF5",
                borderColor: "#1A6EF5",
                borderRadius: "4px",
                px: 2,
                "&:hover": { borderColor: "#1251c0", bgcolor: "rgba(26, 110, 245, 0.04)" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={submitting}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: "#1A6EF5",
                borderRadius: "4px",
                px: 2,
                boxShadow: "none",
                "&:hover": { bgcolor: "#1251c0", boxShadow: "none" },
              }}
            >
              Save changes
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
